import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';

const ClassEditModal = ({ 
  isOpen, 
  onClose, 
  classItem, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    class_title: '',
    subject: '',
    class_forum: '',
    description: '',
    notes: '',
    meeting_link: '',
    start_date: '',
    end_date: '',
    preferred_communication: '',
    status: '',
    assigned_writer: ''
  });
  const [writers, setWriters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingWriters, setLoadingWriters] = useState(false);
  const { user, profile } = useAuth();

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'active', label: 'Active' }
  ];

  // Communication types
  const communicationTypes = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'discord', label: 'Discord' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'teams', label: 'Microsoft Teams' }
  ];

  // Load writers from profiles collection
  useEffect(() => {
    const loadWriters = async () => {
      if (!isOpen) return;
      
      setLoadingWriters(true);
      try {
        const q = query(
          collection(db, 'profiles'),
          where('is_writer', '==', true)
        );
        const querySnapshot = await getDocs(q);
        const writersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setWriters(writersList);
      } catch (error) {
        console.error('Error loading writers:', error);
        toast.error('Failed to load writers');
      } finally {
        setLoadingWriters(false);
      }
    };

    loadWriters();
  }, [isOpen]);

  // Initialize form data when class changes
  useEffect(() => {
    if (classItem) {
      // Safe date formatting function
      const formatDateForInput = (dateValue) => {
        if (!dateValue) return '';
        try {
          const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        } catch (error) {
          console.warn('Error formatting date:', dateValue, error);
          return '';
        }
      };

      setFormData({
        class_title: classItem.class_title || '',
        subject: classItem.subject || '',
        class_forum: classItem.class_forum || '',
        description: classItem.description || '',
        notes: classItem.notes || '',
        meeting_link: classItem.meeting_link || '',
        start_date: formatDateForInput(classItem.start_date),
        end_date: formatDateForInput(classItem.end_date),
        preferred_communication: classItem.preferred_communication || '',
        status: classItem.status || 'pending',
        assigned_writer: classItem.assigned_writer || ''
      });
    }
  }, [classItem]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date) : null,
        end_date: formData.end_date ? new Date(formData.end_date) : null
      };

      await onSave(classItem.id, updates);
      
      // Track edit history for changed fields
      if (classItem && user && profile) {
        const changedFields = Object.keys(updates).filter(key => {
          const oldValue = classItem[key];
          const newValue = updates[key];
          return oldValue !== newValue;
        });

        for (const field of changedFields) {
          try {
            await addDoc(collection(db, 'edit_history'), {
              item_id: classItem.id,
              item_type: 'class',
              field: field,
              old_value: classItem[field] || '',
              new_value: updates[field] || '',
              editor_id: user.uid,
              editor_name: profile.full_name || user.email,
              editor_role: profile.is_admin ? 'admin' : profile.is_writer ? 'writer' : profile.is_qa ? 'qa' : 'user',
              created_at: serverTimestamp()
            });
          } catch (historyError) {
            console.warn('Failed to save edit history for field', field, ':', historyError);
          }
        }
      }
      
      toast.success('Class updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Failed to update class');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form data
    setFormData({
      class_title: '',
      subject: '',
      class_forum: '',
      description: '',
      notes: '',
      meeting_link: '',
      start_date: '',
      end_date: '',
      preferred_communication: '',
      status: '',
      assigned_writer: ''
    });
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Class"
      icon="Edit"
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Class Title *
            </label>
            <input
              type="text"
              name="class_title"
              value={formData.class_title}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Enter class title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Subject *
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Enter subject"
            />
          </div>
        </div>

        {/* Class Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Class Forum
            </label>
            <input
              type="text"
              name="class_forum"
              value={formData.class_forum}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Enter class forum"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value} className="bg-gray-800">
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Communication */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Preferred Communication
          </label>
          <select
            name="preferred_communication"
            value={formData.preferred_communication}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Select communication type</option>
            {communicationTypes.map(type => (
              <option key={type.value} value={type.value} className="bg-gray-800">
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Writer Assignment */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Assigned Writer
          </label>
          <select
            name="assigned_writer"
            value={formData.assigned_writer}
            onChange={handleInputChange}
            disabled={loadingWriters}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
          >
            <option value="">Select writer</option>
            {writers.map(writer => (
              <option key={writer.id} value={writer.id} className="bg-gray-800">
                {writer.full_name} ({writer.email})
              </option>
            ))}
          </select>
          {loadingWriters && (
            <p className="text-sm text-white/60 mt-1">Loading writers...</p>
          )}
        </div>

        {/* Meeting Link */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Meeting Link
          </label>
          <input
            type="url"
            name="meeting_link"
            value={formData.meeting_link}
            onChange={handleInputChange}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="https://zoom.us/j/..."
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder="Enter class description"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder="Any additional notes"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-white/20">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-2 text-white/80 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary hover:bg-primary/80 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Icon name="Save" size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </GlassyModal>
  );
};

export default ClassEditModal;